import { Component, OnInit, ViewChild } from '@angular/core';
import { filter, iif, Observable, switchMap, take } from 'rxjs';

import { UserStoreService } from '@auth/store/services/user-store.service';
import { ProfileStoreService } from '@profile/store/services/profile-store.service';
import { GeneralInfoFormComponent } from '../../components/general-info-form/general-info-form.component';
import { ProfileBaseStoreService } from '../../store/services/profile-base-store.service';
import { AdditionalProfileStoreService } from '@additional-profile/store/services/additional-profile-store.service';
import { Email } from '../../interfaces/customer-info/email';
import { Phone } from '../../interfaces/customer-info/phone';
import { Address } from '../../interfaces/customer-info/address';
import { SettingsStoreService } from '@settings/store/services/settings-store.service';
import { CustomerSettings } from '@settings/models/settings/customer-settings';
import { DestroyableDirective, InViewportDirective } from 'ng-in-viewport';
import { RpcButtonComponent } from '../../../../../core-modules/controls/components/rpc-button/rpc-button.component';
import { GeneralInfoComponent } from '../../components/general-info/general-info.component';
import { ScrollToTopComponent } from '../../../../../core-modules/layout/scroll-to-top/scroll-to-top.component';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'general-info-page',
    templateUrl: './general-info-page.component.html',
    styleUrls: ['./general-info-page.component.scss'],
    imports: [DestroyableDirective, InViewportDirective, RpcButtonComponent, GeneralInfoFormComponent, GeneralInfoComponent, ScrollToTopComponent, AsyncPipe, TranslateModule]
})
export class GeneralInfoPageComponent implements OnInit {
    @ViewChild(GeneralInfoFormComponent) private readonly generalInfoForm: GeneralInfoFormComponent;

    public readonly isPrimaryCustomer$ = this.userStoreService.isPrimaryCustomer$;
    public readonly isSelfProfile$ = this.profileBaseStoreService.isSelfProfile$;
    public emails$: Observable<Email[]>;
    public phones$: Observable<Phone[]>;
    public addresses$: Observable<Address[]>;

    public isEditMode = false;
    public isMinimizedToolbar = false;

    constructor(
        private readonly userStoreService: UserStoreService,
        private readonly profileStoreService: ProfileStoreService,
        private readonly additionalProfileStoreService: AdditionalProfileStoreService,
        private readonly profileBaseStoreService: ProfileBaseStoreService,
        private readonly settingsStoreService: SettingsStoreService,
    ) { }

    public get isFormInvalid(): boolean {
        return this.generalInfoForm?.form?.invalid;
    }

    public ngOnInit(): void {
        this.emails$ = this.profileBaseStoreService.isSelfProfile$.pipe(
            switchMap(isSelfProfile => iif(() => isSelfProfile, this.profileStoreService.emails$, this.additionalProfileStoreService.emails$))
        );

        this.phones$ = this.profileBaseStoreService.isSelfProfile$.pipe(
            switchMap(isSelfProfile => iif(() => isSelfProfile, this.profileStoreService.phones$, this.additionalProfileStoreService.phones$))
        );

        this.addresses$ = this.profileBaseStoreService.isSelfProfile$.pipe(
            switchMap(isSelfProfile => iif(() => isSelfProfile, this.profileStoreService.addresses$, this.additionalProfileStoreService.addresses$))
        );

        this.profileBaseStoreService.loadProfileData();
        this.profileBaseStoreService.loadSettings();
    }

    public onNavigateBack(): void {
        this.profileBaseStoreService.navigateToProfile();
    }

    public onEdit(): void {
        this.isEditMode = true;
    }

    public onCancel(): void {
        this.cancelEdit();
    }

    public onSave(): void {
        const customerInfo = this.generalInfoForm.save();

        if (customerInfo == null) {
            return;
        }

        this.cancelEdit();

        this.profileBaseStoreService.updateCustomerInfo(customerInfo);

        const hasSmsAlertsNumber = customerInfo.phones.some(p => p.isAlertReceiver);

        this.settingsStoreService.getSettings()
            .pipe(take(1), filter(settings => settings != null))
            .subscribe(settings => {
                this.handleSmsAlertSettingsUpdate(settings, hasSmsAlertsNumber);
            });
    }

    public onToolbarMinimizerAnchorViewportChange({ target, visible }: { target: Element; visible: boolean }): void {
        this.isMinimizedToolbar = !visible;
    }

    private cancelEdit(): void {
        this.isEditMode = false;
    }

    private handleSmsAlertSettingsUpdate(settings: CustomerSettings, hasSmsAlertsNumber: boolean): void {
        const isSmsAvailable = hasSmsAlertsNumber;
        const hasExistingAlerts = settings?.smsAlerts?.enableAppointmentAlert || settings?.smsAlerts?.enableListingAlert;
        const noExistingAlerts = !settings?.smsAlerts?.enableAppointmentAlert && !settings?.smsAlerts?.enableListingAlert;

        if (!isSmsAvailable && hasExistingAlerts) {
            this.updateAndSaveSmsSettings(settings, false);
        }

        if (isSmsAvailable && noExistingAlerts) {
            this.updateAndSaveSmsSettings(settings, true);
        }
    }

    private updateAndSaveSmsSettings(settings: CustomerSettings, enable: boolean): void {
        const updatedSettings: CustomerSettings = {
            ...settings,
            smsAlerts: {
                ...settings.smsAlerts,
                enableAppointmentAlert: enable,
                enableListingAlert: enable
            },
            hasSmsAlertsNumber: enable
        };

        this.profileBaseStoreService.updateSettings(updatedSettings);
    }
}
